import { useEffect, useState } from 'react'
import type { MasteryState } from '@/lib/mastery'
import { flashcardFoilClass } from '@/lib/flashcardFoil'

// A flat flashcard showing a concept name, styled identically to a collected
// tile in the Flashcards gallery (the same .flashcard-collected foil ladder,
// via `lib/flashcardFoil.ts`) — no 3D perspective or tilt. Drawn by the
// level-up ceremony (components/ConceptLevelUpCeremony.tsx): a rainbow "snake"
// chases around the border, spinning faster and faster, before the card
// dissolves into the collect flash — or, in a grid of several, lands in place.

// Card size and the type that fits it. `lg` is the lone card; the ceremony's
// grid draws `md` for a short run and `sm` for a long one (lib/levelUpCeremony.ts).
const SIZE = {
  lg: { dims: 'w-52 h-72 sm:w-60 sm:h-80', face: 'gap-3 px-4', name: 'text-xl sm:text-2xl' },
  md: { dims: 'w-32 h-44 sm:w-36 sm:h-48', face: 'gap-2 px-3', name: 'text-base sm:text-lg' },
  sm: { dims: 'w-24 h-32 sm:w-28 sm:h-36', face: 'gap-1.5 px-1.5', name: 'text-[11px] sm:text-xs' },
} as const

interface CollectCard3DProps {
  name: string
  // 'idle' rests flat; 'spin' shows the accelerating rainbow border used by
  // the collection animation; 'won' settles in with a quick pop-in.
  phase?: 'idle' | 'spin' | 'won'
  size?: keyof typeof SIZE
  className?: string
  // When set, the card can be flipped by clicking/tapping it to reveal `back`
  // — a cross-fade between panes, not a 3D flip.
  flippable?: boolean
  back?: React.ReactNode
  // Not yet collected — always shown with the vivid level-3 gallery
  // treatment, so the still-locked card reads as an exciting pack about to
  // open.
  locked?: boolean
  // Once collected, mirrors the Flashcards-tab foil-ring treatment for this
  // concept's real mastery level, so this card matches its gallery tile.
  // Ignored when `locked`.
  mastery?: MasteryState
}

export function CollectCard3D({ name, phase = 'idle', size = 'lg', className = '', flippable = false, back, locked = false, mastery }: CollectCard3DProps) {
  // A locked card always shows the top of the ladder — the still-sealed pack
  // should look like the prize. Otherwise it wears its concept's real level.
  const foilState: MasteryState = locked ? 'level3' : mastery ?? 'new'
  const [side, setSide] = useState<'front' | 'back'>('front')

  // A new concept always opens showing its front, and so does a card that stops
  // being flippable — a ceremony that switches flipping off, which unmounts
  // the back pane, so a card left flipped would spin and dissolve completely
  // blank. The derived side below is what covers the frame the ceremony starts
  // on (this effect only runs after it); the reset keeps the state honest if
  // flipping is switched back on.
  useEffect(() => { setSide('front') }, [name, flippable])

  // Never rest on a back that isn't rendered.
  const shownSide = flippable ? side : 'front'

  const { dims, face, name: nameText } = SIZE[size]
  // While the collection celebration is running, the accelerating snake ring
  // below carries the "rainbow border" effect on its own — the resting foil
  // sheen is dropped so the two don't visually compete.
  const sheenClass = phase === 'spin' ? '' : flashcardFoilClass(true, foilState)
  const phaseClass = phase === 'won' ? 'collect-card-won' : ''

  // A control rendered inside the card's back is its own action: a click, or an Enter/Space on it while focused, must not
  // also flip the card out from under what it opened.
  function fromInnerControl(e: React.SyntheticEvent): boolean {
    const el = e.target as HTMLElement | null
    return !!el?.closest('button, a, input, select, textarea')
  }

  function toggleSide() {
    if (!flippable) return
    setSide(s => (s === 'front' ? 'back' : 'front'))
  }

  return (
    <div
      className={`relative shrink-0 rounded-xl bg-card text-card-foreground ${dims} ${sheenClass} ${phaseClass} ${className} ${flippable ? 'cursor-pointer' : ''}`}
      onClick={e => { if (!fromInnerControl(e)) toggleSide() }}
      role={flippable ? 'button' : undefined}
      tabIndex={flippable ? 0 : undefined}
      aria-label={flippable ? `${name} flashcard, tap to flip` : undefined}
      aria-hidden={flippable ? undefined : true}
      onKeyDown={flippable ? e => {
        if (fromInnerControl(e)) return
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSide() }
      } : undefined}
    >
      <div className="absolute inset-0 rounded-xl overflow-hidden">
        {/* Front — concept name, styled like the gallery tile */}
        <div
          data-card-face="front"
          aria-hidden={shownSide === 'back' || undefined}
          className={`collect-card-pane absolute inset-0 flex flex-col items-center justify-center ${face} text-center ${shownSide === 'back' ? 'collect-card-pane--hidden opacity-0 pointer-events-none' : 'opacity-100'}`}
        >
          {/* A long word on a small card hyphenates rather than overflowing the
              edge; one no dictionary knows breaks where it must. */}
          <span className={`${nameText} font-bold leading-tight text-card-foreground hyphens-auto [overflow-wrap:anywhere]`}>
            {name}
          </span>
        </div>
        {/* Back — the concept's definition, revealed by tapping */}
        {flippable && (
          <div
            data-card-face="back"
            aria-hidden={shownSide === 'front' || undefined}
            className={`collect-card-pane collect-card-back-scroll absolute inset-0 overflow-y-auto px-4 py-5 pointer-events-auto ${shownSide === 'front' ? 'collect-card-pane--hidden opacity-0 pointer-events-none' : 'opacity-100'}`}
          >
            <div className="flex min-h-full flex-col items-center justify-center text-center">
              {back}
            </div>
          </div>
        )}
      </div>
      {/* Rainbow border "snake" — only during the collection celebration */}
      {phase === 'spin' && <span className="collect-card-snake-ring" />}
    </div>
  )
}
